import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import PublicLayout from './components/static/PublicLayout';
import LandingPage from './components/static/LandingPage';
import Login from './components/authentication/Login';
import Register from './components/authentication/Register';
import ForgotPassword from './components/authentication/ForgotPassword';
import ResetPassword from './components/authentication/ResetPassword';
import CmsOverride from './components/static/CmsOverride';
import { AuthProvider } from './auth/AuthContext';
import { RequireRole, PublicOnlyRoute, PageLoader } from './auth/guards';
import { ROLES } from './config/roles';

// Inner public pages and each portal are separate bundles, loaded only when visited.
const AboutPage = lazy(() => import('./components/static/pages/AboutPage'));
const HowItWorksPage = lazy(() => import('./components/static/pages/HowItWorksPage'));
const SendPage = lazy(() => import('./components/static/pages/SendPage'));
const BecomeTravellerPage = lazy(() => import('./components/static/pages/BecomeTravellerPage'));
const SearchPage = lazy(() => import('./components/static/pages/SearchPage'));
const FaqPage = lazy(() => import('./components/static/pages/FaqPage'));
const SafetyPage = lazy(() => import('./components/static/pages/SafetyPage'));
const ProhibitedItemsPage = lazy(() => import('./components/static/pages/ProhibitedItemsPage'));
const ContactPage = lazy(() => import('./components/static/pages/ContactPage'));
const TermsPage = lazy(() => import('./components/static/pages/LegalPages').then((m) => ({ default: m.TermsPage })));
const PrivacyPage = lazy(() => import('./components/static/pages/LegalPages').then((m) => ({ default: m.PrivacyPage })));

const SenderPortal = lazy(() => import('./components/sender/SenderPortal'));
const TravellerPortal = lazy(() => import('./components/traveller/TravellerPortal'));
const AdminPortal = lazy(() => import('./components/admin/AdminPortal'));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={<LandingPage />} />
              <Route path="/about" element={<CmsOverride slug="about" kicker="About us"><AboutPage /></CmsOverride>} />
              <Route path="/how-it-works" element={<CmsOverride slug="how-it-works"><HowItWorksPage /></CmsOverride>} />
              <Route path="/send" element={<SendPage />} />
              <Route path="/become-a-traveller" element={<BecomeTravellerPage />} />
              <Route path="/search" element={<SearchPage />} />
              <Route path="/faqs" element={<FaqPage />} />
              <Route path="/safety" element={<CmsOverride slug="safety"><SafetyPage /></CmsOverride>} />
              <Route path="/prohibited-items" element={<ProhibitedItemsPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/terms" element={<CmsOverride slug="terms" kicker="Legal"><TermsPage /></CmsOverride>} />
              <Route path="/privacy" element={<CmsOverride slug="privacy" kicker="Legal"><PrivacyPage /></CmsOverride>} />
            </Route>
            <Route path="/login" element={<PublicOnlyRoute><Login /></PublicOnlyRoute>} />
            <Route path="/register" element={<PublicOnlyRoute><Register /></PublicOnlyRoute>} />
            <Route path="/forgot-password" element={<PublicOnlyRoute><ForgotPassword /></PublicOnlyRoute>} />
            <Route path="/reset-password" element={<PublicOnlyRoute><ResetPassword /></PublicOnlyRoute>} />
            <Route path="/sender/*" element={<RequireRole roles={[ROLES.SENDER]}><SenderPortal /></RequireRole>} />
            <Route path="/traveller/*" element={<RequireRole roles={[ROLES.TRAVELLER]}><TravellerPortal /></RequireRole>} />
            <Route path="/admin/*" element={<RequireRole roles={[ROLES.ADMIN]}><AdminPortal /></RequireRole>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
